import EditIcon from '@common/icons/EditIcon';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import SVGInline from 'react-svg-inline';
import { LabelWithWrappingContent } from './LabelWithWrappingContent';
import { unitLabel } from '@common/services/unitUtils';

const mortarBoardIcon = require('../../../../images/mortarboard-white-red.svg.raw');
const briefcaseIcon = require('../../../../images/briefcase-white-grey.svg.raw');

export const ExternalCurrencyDetails = ({
  externalCurrency,
  openEditModal,
  allowEditModal
}) => {
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dynamic height: window height minus dialog chrome and other elements
  const dynamicMaxHeight = Math.max(200, windowHeight * 0.4);

  const isVocational = React.useMemo(
    () => externalCurrency.type === 'VocationalPointRequest',
    [externalCurrency]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        mb: 2,
        pb: 2,
        px: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        maxHeight: `${dynamicMaxHeight}px`,
        overflow: 'auto'
      }}
    >
      <div
        style={{ width: 36, height: 36, alignItems: 'center', marginTop: 12 }}
      >
        {isVocational ? (
          <SVGInline svg={briefcaseIcon} />
        ) : (
          <SVGInline svg={mortarBoardIcon} />
        )}
      </div>
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            mt: allowEditModal ? 1 : 2
          }}
        >
          <Typography variant="h5" color={isVocational ? '#494949' : '#dd5151'}>
            {isVocational ? 'Vocational Currency' : 'Professional Practice'}
          </Typography>
          {allowEditModal ? (
            <IconButton
              onClick={openEditModal}
              size="medium"
              style={{ marginRight: -8 }}
            >
              <EditIcon sx={{ color: 'black', fontSize: '2rem' }} />
            </IconButton>
          ) : null}
        </Box>

        <LabelWithWrappingContent
          label={isVocational ? 'Activity:' : 'Domain Name:'}
          content={
            isVocational
              ? externalCurrency.vocational_activity?.name
              : externalCurrency.domain_name
          }
        />

        <LabelWithWrappingContent
          label="Name of the activity:"
          content={externalCurrency.activity_name || ''}
        />

        <LabelWithWrappingContent
          label="Learning Journey Equivalent:"
          content={externalCurrency.learning_journey_equivalent
            .toString()
            .toUpperCase()}
        />

        <LabelWithWrappingContent
          label="Duration:"
          content={`${externalCurrency.activity_duration} ${externalCurrency.activity_interval}`}
        />

        <LabelWithWrappingContent
          label="Date of activity:"
          content={
            externalCurrency.activity_date &&
            new Date(externalCurrency.activity_date).toLocaleDateString('en-AU')
          }
        />

        <LabelWithWrappingContent
          label="Inform Teaching and Assessment Practice:"
          content={externalCurrency.inform_teaching_practice}
        />

        <LabelWithWrappingContent
          label={`Related to ${isVocational ? 'Unit' : 'Course'}/s:`}
          content={[
            ...(externalCurrency.units || []).map(unitLabel),
            externalCurrency.related_to_units,
          ].filter(Boolean).join(', ')}
        />

        <LabelWithWrappingContent
          label="Student Benefit:"
          content={externalCurrency.student_benefit || ''}
        />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            mb: 1
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
            Attachments:
          </Typography>
          {externalCurrency.evidences && externalCurrency.evidences.length > 0
            ? externalCurrency.evidences.map((evidence) => {
                const file = {
                  ...JSON.parse(evidence.file_data),
                  id: evidence.id,
                  fileUrl: evidence.file_url
                };
                return (
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={`file-${file.id}`}
                  >
                    <Typography color="textSecondary">
                      {file.metadata.filename}
                    </Typography>
                  </a>
                );
              })
            : null}
        </Box>
      </Box>
    </Box>
  );
};
